import React from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, CheckCheck, X, AlertCircle, Package, RefreshCw, MessageSquare, Mail, Phone } from "lucide-react";
import { API } from "@/config";

const POLL_INTERVAL = 30_000; // 30 seconds

const CATEGORY_ICON = {
  new_order: <Package className="w-4 h-4" />,
  change_request: <RefreshCw className="w-4 h-4" />,
  cancellation: <X className="w-4 h-4" />,
  payment_issue: <AlertCircle className="w-4 h-4" />,
  failed_message: <MessageSquare className="w-4 h-4" />,
};

const CATEGORY_COLOR = {
  new_order: "text-emerald-400",
  change_request: "text-[#FFD166]",
  cancellation: "text-red-400",
  payment_issue: "text-orange-400",
  failed_message: "text-red-400",
};

function relativeTime(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [counts, setCounts] = React.useState({ unread: 0, action_total: 0 });
  const [tab, setTab] = React.useState("all"); // "all"|"unread"|"action"
  const [notifs, setNotifs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const panelRef = React.useRef(null);

  // Poll counts
  const fetchCounts = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/notifications/counts`);
      setCounts(data);
    } catch (_) {}
  }, []);

  React.useEffect(() => {
    fetchCounts();
    const id = setInterval(fetchCounts, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchCounts]);

  // Load notifications when drawer opens
  React.useEffect(() => {
    if (!open) return;
    loadNotifs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  const loadNotifs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === "unread") params.is_read = false;
      if (tab === "action") params.action_required = true;
      const { data } = await axios.get(`${API}/admin/notifications`, { params });
      setNotifs(data.notifications || []);
    } catch (_) {}
    finally { setLoading(false); }
  };

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const markRead = async (notif) => {
    if (!notif.is_read) {
      await axios.patch(`${API}/admin/notifications/${notif.id}/read`).catch(() => {});
      setNotifs((n) => n.map((x) => x.id === notif.id ? { ...x, is_read: true } : x));
      setCounts((c) => ({ ...c, unread: Math.max(0, c.unread - 1) }));
    }
    if (notif.action_url) {
      setOpen(false);
      // action_url is like /admin/reservations?open=xxx
      const url = new URL(notif.action_url, window.location.origin);
      navigate(url.pathname + url.search);
    }
  };

  const markAllRead = async () => {
    await axios.patch(`${API}/admin/notifications/read-all`).catch(() => {});
    setNotifs((n) => n.map((x) => ({ ...x, is_read: true })));
    setCounts((c) => ({ ...c, unread: 0 }));
  };

  const resolve = async (e, notif) => {
    e.stopPropagation();
    await axios.patch(`${API}/admin/notifications/${notif.id}/resolve`).catch(() => {});
    setNotifs((n) => n.map((x) => x.id === notif.id ? { ...x, status: "resolved", action_required: false } : x));
    setCounts((c) => ({ ...c, action_total: Math.max(0, c.action_total - 1) }));
    fetchCounts();
  };

  const badge = counts.unread;

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative flex items-center justify-center w-9 h-9 rounded-full hover:bg-white/10 transition-colors"
        aria-label="Notifications"
        data-testid="notif-bell-btn"
      >
        <Bell className="w-5 h-5 text-[#FFD166]" />
        {badge > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold leading-none"
            data-testid="notif-badge"
          >
            {badge > 99 ? "99+" : badge}
          </span>
        )}
        {counts.action_total > 0 && badge === 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-orange-400 border-2 border-[#050D14]" />
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 w-[360px] max-w-[95vw] bg-[#0C1822] border border-[#FFD166]/20 rounded-xl shadow-2xl z-[200] flex flex-col"
          style={{ maxHeight: "80vh" }}
          data-testid="notif-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#FFD166]/15">
            <span className="font-serif text-[#FFD166] text-sm font-semibold">Notifications</span>
            <div className="flex items-center gap-1">
              {counts.unread > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="flex items-center gap-1 text-xs text-[#B9C8D5] hover:text-[#FFD166] px-2 py-1 rounded"
                  data-testid="notif-mark-all-read"
                >
                  <CheckCheck className="w-3 h-3" /> All read
                </button>
              )}
              <button type="button" onClick={() => setOpen(false)} className="text-[#B9C8D5] hover:text-white px-1">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-[#FFD166]/10">
            {[["all", "All"], ["unread", `Unread${badge > 0 ? ` (${badge})` : ""}`], ["action", `Action Needed${counts.action_total > 0 ? ` (${counts.action_total})` : ""}`]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`flex-1 py-2 text-xs font-medium transition-colors ${tab === key ? "text-[#FFD166] border-b-2 border-[#FFD166]" : "text-[#B9C8D5] hover:text-white"}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1 divide-y divide-[#FFD166]/8">
            {loading ? (
              <div className="flex items-center justify-center py-10 text-[#B9C8D5] text-sm">Loading…</div>
            ) : notifs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-[#B9C8D5] text-sm gap-2">
                <Bell className="w-8 h-8 opacity-30" />
                <span>No notifications</span>
              </div>
            ) : (
              notifs.map((n) => (
                <div
                  key={n.id}
                  onClick={() => markRead(n)}
                  className={`px-4 py-3 cursor-pointer hover:bg-white/5 transition-colors ${!n.is_read ? "bg-[#FFD166]/5" : ""}`}
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    <span className={`mt-0.5 shrink-0 ${CATEGORY_COLOR[n.category] || "text-[#B9C8D5]"}`}>
                      {CATEGORY_ICON[n.category] || <Bell className="w-4 h-4" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                        <span className="text-xs font-semibold text-[#F6F1E4]">{n.title}</span>
                        {n.action_required && n.status !== "resolved" && (
                          <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/30">Action needed</span>
                        )}
                        {!n.is_read && (
                          <span className="w-1.5 h-1.5 rounded-full bg-[#FFD166] shrink-0" />
                        )}
                      </div>
                      {n.order_number && (
                        <span className="text-[11px] text-[#B9C8D5]">Order #{n.order_number}{n.customer_name ? ` — ${n.customer_name}` : ""}</span>
                      )}
                      <p className="text-[11px] text-[#B9C8D5] mt-0.5 line-clamp-2">{n.message}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-[10px] text-[#B9C8D5]/60">{relativeTime(n.created_at)}</span>
                        {n.action_required && n.status !== "resolved" && (
                          <button
                            type="button"
                            onClick={(e) => resolve(e, n)}
                            className="text-[10px] text-[#B9C8D5] hover:text-[#FFD166] px-2 py-0.5 rounded border border-[#FFD166]/20 hover:border-[#FFD166]/50"
                          >
                            Resolve
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-[#FFD166]/15 px-4 py-2">
            <button
              type="button"
              onClick={() => { setOpen(false); navigate("/admin/notifications"); }}
              className="text-xs text-[#FFD166] hover:underline w-full text-center"
              data-testid="notif-view-all"
            >
              View All Notifications →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
